import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import * as React from "react";

import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from "@ando/ui";

import type { AdminUser } from "@ando/core";

import {
  AuditFooter,
  InspectBanner,
  UserDetailStackedTabs,
  type UserDetailTabId,
  UserHeaderCard,
  useInspectMode,
} from "./UserDetailContent";

/**
 * UserDetailDrawer — right-side slide-in panel showing a single user's
 * Overview / Sessioni / MFA / Trace recenti / PII Inspect tabs.
 *
 * This is the CROSS-CUTTING drill-down used from inside the Org detail's
 * Members tab, where a floating panel over the current page is the right
 * affordance. The full-page admin destination at ``/admin/users/$userId``
 * uses ``UserDetailShell`` instead (full-width inline, mirroring
 * ``OrgDetailShell``). Both compose the SAME tabs + Inspect-Mode state from
 * ``UserDetailContent`` so behaviour stays defined once.
 *
 * Inspect Mode close contract: closing the drawer mid-elevation surfaces a
 * ``console.warn`` so the audit pipeline can pick it up downstream once the
 * BE revoke endpoint ships (state is discarded on unmount so we can't push
 * an audit row from here).
 */

interface UserDetailDrawerProps {
  open: boolean;
  /** Already-listed row, when the drawer was opened from a table. */
  user: AdminUser | null;
  userId: string | null;
  onClose: () => void;
}

export function UserDetailDrawer(props: UserDetailDrawerProps): React.ReactElement {
  const { open, user, userId, onClose } = props;
  const { i18n } = useLingui();
  const [tab, setTab] = React.useState<UserDetailTabId>("overview");
  const inspect = useInspectMode(userId);

  // Reset the active tab every time the drawer opens for a new user; the
  // Inspect-Mode window/log reset lives in ``useInspectMode`` (keyed on the
  // user id).
  React.useEffect(() => {
    if (!open) return;
    setTab("overview");
  }, [open, userId]);

  // Revocation on drawer close — required by the security contract. State is
  // discarded on unmount so we surface a console.warn rather than a (lost)
  // audit-log push; the BE revoke endpoint will extend the trail server-side.
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (wasOpenRef.current && !open && inspect.inspectExpiresAt !== null) {
      console.warn("[admin-users] PII Inspect revoked because drawer closed mid-elevation", {
        userId,
      });
    }
    wasOpenRef.current = open;
  }, [open, inspect.inspectExpiresAt, userId]);

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DrawerContent
        side="right"
        className="w-full sm:max-w-[640px]"
        ariaTitle={i18n._(msg`Dettaglio utente`)}
      >
        {user || userId ? (
          <>
            <DrawerHeader>
              <DrawerTitle className="text-[14px]">
                <Trans>Dettaglio utente</Trans>
              </DrawerTitle>
              <div className="mt-3">
                <UserHeaderCard user={user} userId={userId} inspectActive={inspect.inspectActive} />
              </div>
            </DrawerHeader>
            {inspect.inspectActive && inspect.inspectExpiresAt !== null ? (
              <div className="sticky top-0 z-10">
                <InspectBanner
                  expiresAt={inspect.inspectExpiresAt}
                  now={inspect.now}
                  onRevoke={inspect.revoke}
                />
              </div>
            ) : null}
            <DrawerBody>
              <UserDetailStackedTabs
                user={user}
                userId={userId}
                tab={tab}
                onTabChange={setTab}
                inspect={inspect}
              />
            </DrawerBody>
            <AuditFooter lines={inspect.auditLog} />
          </>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}
